import { generateNKeysBetween } from 'fractional-indexing';

/**
 * "Two people added rows at the same place" (POSITION_CLASH_PLAN.md, 2026-09-29).
 *
 * The server refuses such a save with 409 POSITION_CLASH (nothing written) and tells us, per place:
 * my rows' keys, every row now between the two anchors (gapKeys) and the other person's rows. Here we
 * give my rows NEW keys so they sit together after the other rows (B_FIRST — the default, "mine after
 * theirs") or before them (A_FIRST), then the caller saves again and records the marks.
 *
 * Row order is the whole point, so new keys are only ever made between two ADJACENT keys (nothing
 * else lies between them), with the same key rules each grid already uses:
 *  - Round: the `fractional-indexing` library the Round grid uses.
 *  - Square: a midpoint on the Square grid's own alphabet (0-9 A-Z a-z, plain text order), which never
 *    ends a key with '0' (a key ending in '0' leaves no room before it).
 * If anything fails, placeRows() returns null and the caller saves the rows where they were (as today).
 */
export interface PositionClash {
  anchorAfter: string;
  anchorBefore: string;
  mineKeys: string[];
  gapKeys: string[];
  theirs: Array<{ orderKey: string; createdBy?: string; createdByName?: string; length?: number; girth?: number;
    width?: number; thick?: number; pieces?: number }>;
}

export interface PositionClashBody {
  code: 'POSITION_CLASH';
  tallyType: 'ROUND' | 'SQUARE' | string;
  clashes: PositionClash[];
}

export type PlaceChoice = 'B_FIRST' | 'A_FIRST';

export interface PlacedRows {
  /** The rows to save again: my clashing rows with new keys; every row without position fields. */
  rows: any[];
  /** old key → new key, for my placed rows. */
  moved: Record<string, string>;
  /** Body for POST /tally/row-clash/record. */
  record: { masterId: number; choice: string; clashes: Array<{ mine: Array<{ key: string; movedFrom: string }>; theirs: string[] }> };
  /** How many of my rows were placed, and whose rows they were placed next to (for the message). */
  placedCount: number;
  otherNames: string[];
}

export function isPositionClash(err: any): boolean {
  return err?.status === 409 && err?.error?.code === 'POSITION_CLASH' && Array.isArray(err?.error?.clashes);
}

/** Drops the position fields so a save is not checked for position again. */
export function withoutPositionFields(rows: any[]): any[] {
  return (rows || []).map((r) => {
    const { anchorAfter, anchorBefore, seenUpTo, ...rest } = r || {};
    return rest;
  });
}

export function placeRows(rows: any[], body: PositionClashBody, choice: PlaceChoice = 'B_FIRST'): PlacedRows | null {
  try {
    const masterId = rows?.[0]?.id?.tallyMasterId;
    const moved: Record<string, string> = {};
    const record: PlacedRows['record'] = { masterId, choice, clashes: [] };
    const names = new Set<string>();
    let placedCount = 0;

    for (const clash of body.clashes || []) {
      const mine = [...(clash.mineKeys || [])].sort(textOrder);
      const gap = [...(clash.gapKeys || [])].sort(textOrder);
      if (!mine.length || !gap.length) continue;
      const left = choice === 'A_FIRST' ? (clash.anchorAfter || null) : gap[gap.length - 1];
      const right = choice === 'A_FIRST' ? gap[0] : (clash.anchorBefore || null);
      const fresh = keysBetween(body.tallyType, left, right, mine.length);
      mine.forEach((old, i) => { moved[old] = fresh[i]; });
      record.clashes.push({
        mine: mine.map((old, i) => ({ key: fresh[i], movedFrom: old })),
        theirs: (clash.theirs || []).map((t) => t.orderKey),
      });
      (clash.theirs || []).forEach((t) => names.add(t.createdByName || t.createdBy || ''));
      placedCount += mine.length;
    }

    const out = withoutPositionFields(rows).map((r) => {
      const to = moved[r?.id?.orderKey];
      return to ? { ...r, id: { ...r.id, orderKey: to } } : r;
    });
    return { rows: out, moved, record, placedCount, otherNames: [...names].filter(Boolean) };
  } catch {
    return null;
  }
}

/** `count` keys in increasing order, strictly between left and right (null = open end). */
export function keysBetween(tallyType: string, left: string | null, right: string | null, count: number): string[] {
  let keys: string[];
  if (tallyType === 'SQUARE') {
    // New Square tallies use the same library keys as Round. A tally made before 2026-10-01 has old-style keys
    // the library refuses — then the Square alphabet midpoint below is used, as before.
    try {
      keys = generateNKeysBetween(left, right, count);
    } catch {
      keys = squareKeysBetween(left || '', right, count);
    }
  } else {
    keys = generateNKeysBetween(left, right, count);
  }
  // Safety net: never hand back keys that would break the order.
  let prev = left;
  for (const k of keys) {
    if (!k || (prev && !(prev < k)) || (right && !(k < right))) throw new Error('bad key order');
    prev = k;
  }
  return keys;
}

// ── Square keys ──────────────────────────────────────────────────────────────────────────────
const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

function squareKeysBetween(left: string, right: string | null, count: number): string[] {
  if (count <= 0) return [];
  if (count === 1) return [squareMidpoint(left, right)];
  const half = Math.floor(count / 2);
  const mid = squareMidpoint(left, right);
  return [...squareKeysBetween(left, mid, half), mid, ...squareKeysBetween(mid, right, count - half - 1)];
}

/** A key strictly between a and b (b null = no upper end), never ending in '0'. */
function squareMidpoint(a: string, b: string | null): string {
  if (b !== null && !(a < b)) throw new Error('left must be before right');
  if (b !== null) {
    // Shared start: keep it, then find a midpoint of the rest.
    let n = 0;
    while (n < b.length && (a[n] ?? DIGITS[0]) === b[n]) n++;
    if (n > 0) return b.slice(0, n) + squareMidpoint(a.slice(n), b.slice(n));
  }
  const digitA = a ? DIGITS.indexOf(a[0]) : 0;
  const digitB = b !== null ? DIGITS.indexOf(b[0]) : DIGITS.length;
  if (digitA < 0 || digitB < 0) throw new Error('unknown key character');
  if (digitB - digitA > 1) return DIGITS[Math.round((digitA + digitB) / 2)];
  // Adjacent first characters: take a's first character and go deeper after it.
  if (b !== null && b.length > 1) return b.slice(0, 1);
  return DIGITS[digitA] + squareMidpoint(a.slice(1), null);
}

function textOrder(x: string, y: string): number {
  return x < y ? -1 : x > y ? 1 : 0;
}
